import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { useRemoteStrings } from '@acorns/web-utils'

import { ProductKey } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'

import {
  AccountTypesHeader,
  BenefitIcon,
  ListHeading,
  TierBenefitTitle,
  TierBenefitsContainer,
  TierBenefitsList,
  TierBenefitsListItem,
} from './styled-components'

type Props = {
  tierProducts: ProductKey[]
  highestTierProducts: ProductKey[]
  passionsEligible: boolean
}

const BenefitsSection: React.FunctionComponent<Props> = ({
  tierProducts,
  highestTierProducts,
  passionsEligible,
}) => {
  const { getRemoteString } = useRemoteStrings()
  const allProductsAndBenefits = [
    {
      productKey: ProductKey.Spend,
      header: getRemoteString({ key: 'registration.moc_tier.list_1.title' }),
      icon: AcornsIcon.Icon.ProductBankingFv,
      items: [
        {
          benefits: [
            getRemoteString({ key: 'registration.moc_tier.list_1.item_1' }),
            getRemoteString({ key: 'registration.moc_tier.list_1.item_2' }),
            getRemoteString({ key: 'registration.moc_tier.list_1.item_3' }),
          ],
        },
        {
          productKey: ProductKey.EmergencyFund,
          benefits: [getRemoteString({ key: 'registration.moc_tier.list_1.item_4' })],
        },
        {
          productKey: ProductKey.MightyOakCard,
          benefits: [getRemoteString({ key: 'registration.moc_tier.list_1.item_5' })],
        },
      ],
    },
    {
      productKey: ProductKey.Invest,
      header: getRemoteString({ key: 'registration.moc_tier.list_2.title' }),
      icon: AcornsIcon.Icon.ProductInvestHubFv,
      items: [
        {
          benefits: [getRemoteString({ key: 'registration.moc_tier.list_2.item_1' })],
        },
        {
          productKey: ProductKey.Later,
          benefits: [getRemoteString({ key: 'registration.moc_tier.list_2.item_2' })],
        },
        {
          productKey: ProductKey.Early,
          benefits: [getRemoteString({ key: 'registration.moc_tier.list_2.item_3' })],
        },
      ],
    },
    {
      productKey: ProductKey.Earn,
      header: getRemoteString({ key: 'registration.moc_tier.list_3.title' }),
      icon: AcornsIcon.Icon.ProductEarnFv,
      items: [
        {
          benefits: [
            getRemoteString({ key: 'registration.moc_tier.list_3.item_1' }),
            getRemoteString({ key: 'registration.moc_tier.list_3.item_2' }),
          ],
        },
      ],
    },
    ...(passionsEligible
      ? [
          {
            productKey: ProductKey.SelfDirectedInvest,
            header: getRemoteString({ key: 'registration.moc_tier.list_4.title' }),
            icon: AcornsIcon.Icon.UtilityCustomizeFv,
            items: [
              {
                benefits: [
                  getRemoteString({ key: 'registration.moc_tier.list_4.item_1' }),
                  getRemoteString({ key: 'registration.moc_tier.list_4.item_2' }),
                  getRemoteString({ key: 'registration.moc_tier.list_4.item_3' }),
                ],
              },
            ],
          },
        ]
      : []),
    {
      productKey: ProductKey.PremiumEducation,
      header: getRemoteString({ key: 'registration.moc_tier.list_7.title' }),
      icon: AcornsIcon.Icon.ProductGrowFv,
      items: [
        {
          benefits: [
            getRemoteString({ key: 'registration.moc_tier.list_7.item_1' }),
            getRemoteString({ key: 'registration.moc_tier.list_7.item_2' }),
            getRemoteString({ key: 'registration.moc_tier.list_7.item_3' }),
          ],
        },
      ],
    },
    {
      productKey: ProductKey.BenefitsWillStandard,
      header: getRemoteString({ key: 'registration.moc_tier.list_6.title' }),
      icon: AcornsIcon.Icon.Benefits,
      items: [
        {
          benefits: [getRemoteString({ key: 'registration.moc_tier.list_6.item_2' })],
        },
      ],
    },
  ]

  const availableProductsAndBenefits: any = []

  allProductsAndBenefits.forEach(
    product =>
      highestTierProducts.includes(product?.productKey) &&
      availableProductsAndBenefits.push(product),
  )
  return (
    <TierBenefitsContainer>
      {availableProductsAndBenefits.map((benefitSection, index) => (
        <>
          <AccountTypesHeader>
            <TierBenefitTitle
              data-testid={`${TestIDs.PRICING_INTERSTITIAL.MOC_BENEFIT_TITLE}_${index}`}
            >
              <BenefitIcon icon={benefitSection.icon} width="24px" height="24px" />
              <ListHeading>{benefitSection.header}</ListHeading>
            </TierBenefitTitle>
          </AccountTypesHeader>
          <TierBenefitsList>
            {benefitSection.items.map((item, i) =>
              item.benefits?.map(benefit => (
                <TierBenefitsListItem
                  key={`${benefitSection.header}-${i}`}
                  available={tierProducts.includes(
                    item.productKey ? item.productKey : benefitSection.productKey,
                  )}
                >
                  {benefit}
                </TierBenefitsListItem>
              )),
            )}
          </TierBenefitsList>
        </>
      ))}
    </TierBenefitsContainer>
  )
}

export default BenefitsSection
